import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { TableColumn } from 'src/app/components/table';
import {
  EditionFeedbackAutomationService,
  EditionFeedbackHistoryItem,
  EditionFeedbackSchedulerStatus
} from 'src/app/services/edition-feedback-automation.service';
import { StorageService } from 'src/app/services/auth/storage.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface EditionFeedbackHistoryRow {
  id: number;
  executedAt: string;
  executedAtSort: number;
  triggeredBy: string;
  referenceMonth: string;
  subscriptionTitleName: string;
  editionId: number;
  theme: string;
  productId: number;
  formId: number;
  formTitle: string;
  status: 'CREATED' | 'SKIPPED' | 'FAILED';
  errorMessage?: string;
}

@Component({
  selector: 'app-edition-feedback-automation',
  templateUrl: './edition-feedback-automation.component.html',
  styleUrl: './edition-feedback-automation.component.scss',
  providers: [ConfirmationService]
})
export class EditionFeedbackAutomationComponent implements OnInit {
  private editionFeedbackAutomationService = inject(EditionFeedbackAutomationService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  historyCols: TableColumn[] = [];
  historyRows: EditionFeedbackHistoryRow[] = [];
  filteredHistoryRows: EditionFeedbackHistoryRow[] = [];

  historyFilterForm = new FormGroup({
    referenceMonth: new FormControl<string>('all', { nonNullable: true }),
    status: new FormControl<string>('all', { nonNullable: true }),
    triggeredBy: new FormControl<string>('all', { nonNullable: true })
  });

  monthFilterOptions = [{ label: 'Todos os meses', value: 'all' }];

  statusFilterOptions = [
    { label: 'Todos os status', value: 'all' },
    { label: 'Criado', value: 'CREATED' },
    { label: 'Ignorado', value: 'SKIPPED' },
    { label: 'Falha', value: 'FAILED' }
  ];

  triggeredByFilterOptions = [
    { label: 'Todas as origens', value: 'all' },
    { label: 'Agendador', value: 'scheduler' },
    { label: 'Manual', value: 'manual' }
  ];

  schedulerStatus: EditionFeedbackSchedulerStatus | undefined;
  isActionLoading = false;
  isHistoryLoading = false;
  provisionAvailable = true;
  resultDialogVisible = false;
  lastRunSummary = '';
  lastRunErrors: string[] = [];

  async ngOnInit(): Promise<void> {
    this.historyCols = this.buildHistoryCols();
    await Promise.all([this.loadSchedulerStatus(), this.loadHistory()]);
  }

  async loadSchedulerStatus(): Promise<void> {
    try {
      this.schedulerStatus = await lastValueFrom(
        this.editionFeedbackAutomationService.getSchedulerStatus()
      );
      this.provisionAvailable = this.schedulerStatus.provisionAvailable !== false;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async loadHistory(): Promise<void> {
    this.isHistoryLoading = true;
    try {
      const items = await lastValueFrom(this.editionFeedbackAutomationService.getHistory());
      this.historyRows = this.sortHistoryRows(items.map((item) => this.toHistoryRow(item)));
      this.refreshMonthFilterOptions(items);
      this.applyHistoryFilters();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.isHistoryLoading = false;
    }
  }

  applyHistoryFilters(): void {
    const { referenceMonth, status, triggeredBy } = this.historyFilterForm.getRawValue();

    this.filteredHistoryRows = this.sortHistoryRows(
      this.historyRows.filter((row) => {
        const monthOk = referenceMonth === 'all' || row.referenceMonth === referenceMonth;
        const statusOk = status === 'all' || row.status === status;
        const triggerOk = triggeredBy === 'all' || row.triggeredBy === triggeredBy;
        return monthOk && statusOk && triggerOk;
      })
    );
  }

  confirmProvision(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'Deseja executar a automação agora? Serão criados e publicados os formulários de feedback de edição para as assinaturas elegíveis.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Executar',
      rejectLabel: 'Cancelar',
      accept: () => this.runProvision()
    });
  }

  private async runProvision(): Promise<void> {
    LoaderService.showLoader();
    this.isActionLoading = true;
    try {
      const response = await lastValueFrom(
        this.editionFeedbackAutomationService.provision(`user-${StorageService.userId}`)
      );
      const created = response.createdCount ?? 0;
      const skipped = response.skippedCount ?? 0;
      const failed = response.failedCount ?? 0;
      this.lastRunSummary = `Criados: ${created} · Ignorados: ${skipped} · Falhas: ${failed}`;
      this.lastRunErrors = response.errorSummaries ?? [];

      this.messageService.add({
        severity: failed > 0 ? 'warn' : 'success',
        summary: failed > 0 ? 'Automação com pendências' : 'Automação executada',
        detail: this.lastRunSummary
      });

      if (failed > 0) {
        this.resultDialogVisible = true;
      }

      await this.loadHistory();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private toHistoryRow(item: EditionFeedbackHistoryItem): EditionFeedbackHistoryRow {
    const executedAtSort = this.toExecutedAtSort(item.createdAt);

    return {
      id: item.id ?? 0,
      executedAt: this.formatDateTime(item.createdAt),
      executedAtSort,
      triggeredBy: item.triggeredBy ?? 'manual',
      referenceMonth: item.referenceMonth ?? '-',
      subscriptionTitleName: item.subscriptionTitleName ?? '-',
      editionId: item.editionId ?? 0,
      theme: item.theme ?? '-',
      productId: item.productId ?? 0,
      formId: item.formId ?? 0,
      formTitle: item.formTitle ?? '-',
      status: this.normalizeStatus(item.status),
      errorMessage: item.errorMessage
    };
  }

  private normalizeStatus(status?: string): EditionFeedbackHistoryRow['status'] {
    if (status === 'CREATED' || status === 'SKIPPED' || status === 'FAILED') {
      return status;
    }
    return 'FAILED';
  }

  private sortHistoryRows(rows: EditionFeedbackHistoryRow[]): EditionFeedbackHistoryRow[] {
    return [...rows].sort((a, b) => {
      const dateDiff = b.executedAtSort - a.executedAtSort;
      if (dateDiff !== 0) {
        return dateDiff;
      }
      return b.id - a.id;
    });
  }

  private toExecutedAtSort(value?: string): number {
    if (!value) {
      return 0;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  private formatDateTime(value?: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private refreshMonthFilterOptions(items: EditionFeedbackHistoryItem[]): void {
    const months = [
      ...new Set(
        items
          .map((item) => item.referenceMonth)
          .filter((month): month is string => !!month)
      )
    ].sort((a, b) => b.localeCompare(a));

    this.monthFilterOptions = [
      { label: 'Todos os meses', value: 'all' },
      ...months.map((month) => ({ label: month, value: month }))
    ];
  }

  private buildHistoryCols(): TableColumn[] {
    return [
      new TableColumn.Builder()
        .setHeader('Data')
        .setField('executedAtSort')
        .setType('number')
        .setFilter(false)
        .setSortable(true)
        .setDisplayFunction((item: EditionFeedbackHistoryRow) => item.executedAt)
        .build(),
      new TableColumn.Builder()
        .setHeader('Origem')
        .setField('triggeredBy')
        .setFilter(false)
        .setSortable(false)
        .setDisplayFunction((item: EditionFeedbackHistoryRow) =>
          item.triggeredBy === 'scheduler' ? 'Agendador' : 'Manual'
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Mês')
        .setField('referenceMonth')
        .setFilter(false)
        .setSortable(true)
        .build(),
      new TableColumn.Builder()
        .setHeader('Assinatura')
        .setField('subscriptionTitleName')
        .setFilter(false)
        .setSortable(true)
        .build(),
      new TableColumn.Builder()
        .setHeader('Edição')
        .setField('editionId')
        .setType('number')
        .setFilter(false)
        .setSortable(false)
        .build(),
      new TableColumn.Builder()
        .setHeader('Tema')
        .setField('theme')
        .setFilter(false)
        .setSortable(false)
        .build(),
      new TableColumn.Builder()
        .setHeader('Produto')
        .setField('productId')
        .setRouterLink('/products/catalog/product/')
        .setRouterLinkFieldName('productId')
        .setType('number')
        .setFilter(false)
        .setSortable(false)
        .build(),
      new TableColumn.Builder()
        .setHeader('Formulário')
        .setField('formTitle')
        .setRouterLink('/research/feedbacks/')
        .setRouterLinkFieldName('formId')
        .setLinkActive((item: EditionFeedbackHistoryRow) => item.formId > 0)
        .setFilter(false)
        .setSortable(false)
        .setDisplayFunction((item: EditionFeedbackHistoryRow) =>
          item.formTitle && item.formTitle !== '-' ? item.formTitle : '-'
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Status')
        .setField('status')
        .setFilter(false)
        .setSortable(false)
        .setInnerHTML((item: EditionFeedbackHistoryRow) =>
          this.getHistoryStatusHtml(item.status)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Motivo')
        .setField('errorMessage')
        .setFilter(false)
        .setSortable(false)
        .setDisplayFunction((item: EditionFeedbackHistoryRow) =>
          item.errorMessage ?? '-'
        )
        .build()
    ];
  }

  private getHistoryStatusHtml(status: EditionFeedbackHistoryRow['status']): string {
    switch (status) {
      case 'CREATED':
        return '<span class="edition-feedback-automation-status edition-feedback-automation-status--create">Criado</span>';
      case 'SKIPPED':
        return '<span class="edition-feedback-automation-status edition-feedback-automation-status--skipped">Ignorado</span>';
      case 'FAILED':
        return '<span class="edition-feedback-automation-status edition-feedback-automation-status--failed">Falha</span>';
      default:
        return '<span class="edition-feedback-automation-status edition-feedback-automation-status--unknown">-</span>';
    }
  }
}
